import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { isAfter, parseISO } from "date-fns";
import { levelSortOrder } from "@/utils/utils";
import { RootState } from '~/types/store'
import { UserSubject, TestAttempt } from '~/types/user'

interface Topic {
  id: number | string
  name: string
  newContent?: boolean
  learnedCards?: number
  totalLearnableCards?: number
  [key: string]: unknown
}

interface Card {
  id: number | string
  newContent?: boolean
  userHasLearned?: boolean
  isDownload?: boolean
  topics?: Topic[]
  [key: string]: unknown
}

interface CardCollection {
  cards: Card[]
  availableTopics: Topic[]
  [key: string]: unknown
}

interface SubjectTest {
  id: number | string
  latestAttempt?: TestAttempt | null
  [key: string]: unknown
}

interface CurrentSubjectStream {
  tests?: SubjectTest[]
  correctionsCardId?: number | string
  [key: string]: unknown
}

interface CurrentSubject {
  id?: number | string
  slug?: string
  stream?: CurrentSubjectStream
  latestTestAttempt?: TestAttempt | null
  [key: string]: unknown
}

interface SubjectTestDetail {
  latestAttempt?: TestAttempt | null
  [key: string]: unknown
}

interface SubjectFilter {
  name: string
  [key: string]: unknown
}

export interface SubjectsState {
  subjects: UserSubject[]
  primarySubjects: UserSubject[]
  currentSubject: CurrentSubject
  currentSubjectSection: string
  currentSubjectLearnCards: CardCollection | Record<string, never>
  currentSubjectLearnLoading: boolean
  currentSubjectLearnShowCheckedCards: boolean
  currentSubjectProgressCards: CardCollection | Record<string, never>
  currentSubjectUsefulCards: CardCollection | Record<string, never>
  currentCard: unknown[]
  selectedTopics: Topic[]
  selectedParentTopics: Topic[]
  selectedSections: SubjectFilter[]
  selectedExamTypes: unknown[]
  selectedDifficultyLevels: unknown[]
  subjectTest: SubjectTestDetail | Record<string, never>
  subjectTests?: unknown
  subjectTestAnswers: unknown[]
  modalFixedBody: boolean
  unfilteredCards: unknown[]
}

export function state (): SubjectsState {
  return ({
  subjects: [],
  primarySubjects: [],
  currentSubject: {},
  currentSubjectSection: "",
  currentSubjectLearnCards: {},
  currentSubjectLearnLoading: false,
  currentSubjectLearnShowCheckedCards: false,
  currentSubjectProgressCards: [] as unknown as CardCollection,
  currentSubjectUsefulCards: [] as unknown as CardCollection,
  currentCard: [],
  selectedTopics: [],
  selectedParentTopics: [],
  selectedSections: [],
  selectedExamTypes: [],
  selectedDifficultyLevels: [],
  subjectTest: {},
  subjectTestAnswers: [],
  modalFixedBody: true,
  unfilteredCards: [],
})
}

export const mutations: MutationTree<SubjectsState> = {
  SET_UNFILTERED_CARDS (state, cards: unknown[]) {
    state.unfilteredCards = cards;
  },
  SET_SUBJECTS (state, data: UserSubject[]) {
    state.subjects = data;
  },
  SET_PRIMARY_SUBJECTS (state, data: UserSubject[]) {
    state.primarySubjects = data;
  },
  SET_CURRENT_SUBJECT (state, data: CurrentSubject) {
    state.currentSubject = data;
  },
  SET_CURRENT_SUBJECT_SECTION (state, data: string) {
    state.currentSubjectSection = data;
  },
  SET_CURRENT_SUBJECT_LEARN_CARDS (state, data: CardCollection) {
    state.currentSubjectLearnCards = data;
  },
  SET_CURRENT_SUBJECT_LEARN_LOADING (state, data: boolean) {
    state.currentSubjectLearnLoading = data;
  },
  SET_CURRENT_SUBJECT_LEARN_SHOW_CHECKED_CARDS (state, data: boolean) {
    state.currentSubjectLearnShowCheckedCards = data;
  },
  SET_CURRENT_SUBJECT_PROGRESS_CARDS (state, data: CardCollection) {
    state.currentSubjectProgressCards = data;
  },
  SET_CURRENT_SUBJECT_USEFUL_CARDS (state, data: CardCollection) {
    state.currentSubjectUsefulCards = data;
  },
  SET_CURRENT_CARD (state, data: unknown[]) {
    state.currentCard = data;
  },
  SET_SELECTED_TOPIC_FILTERS (state, topics: Topic[]) {
    state.selectedTopics = topics;
  },
  SET_SELECTED_PARENT_TOPIC_FILTERS (state, parentTopics: Topic[]) {
    state.selectedParentTopics = parentTopics;
  },
  SET_SELECTED_SECTION_FILTERS (state, sections: SubjectFilter[]) {
    state.selectedSections = sections;
  },
  SET_SELECTED_DIFFICULTY_FILTERS (state, difficultyLevels: unknown[]) {
    state.selectedDifficultyLevels = difficultyLevels;
  },
  SET_SELECTED_EXAM_TYPE_FILTERS (state, examTypeFilters: unknown[]) {
    state.selectedExamTypes = examTypeFilters;
  },
  SET_MODAL_FIXED_BODY (state, data: boolean) {
    state.modalFixedBody = data;
  },
  SET_SUBJECT_TESTS (state, data: unknown) {
    state.subjectTests = data;
  },
  SET_SUBJECT_TEST (state, data: SubjectTestDetail) {
    state.subjectTest = data;
  },
  SET_SUBJECT_TEST_RESULT (state, latestAttempt: TestAttempt) {
    state.subjectTest = {
      ...state.subjectTest,
      latestAttempt,
    };
  },
  SET_SUBJECT_TEST_ANSWERS (state, answers: unknown[]) {
    state.subjectTestAnswers = answers;
  },
  UPDATE_CURRENT_SUBJECT (state, { testId, updatedTestAttempt }: { testId: number | string, updatedTestAttempt: TestAttempt }) {
    const updatedTestIndex = state.currentSubject.stream?.tests?.findIndex(
      (test) => test.id === testId
    );
    if (updatedTestIndex !== -1 && state.currentSubject.stream?.tests) {
      state.currentSubject.stream.tests[updatedTestIndex as number].latestAttempt =
        updatedTestAttempt;
    }

    state.currentSubject.latestTestAttempt = updatedTestAttempt;
  },
  UPDATE_NEW_CONTENT_STATUS_TOPIC (state, cardId: number | string) {
    if (state.currentSubjectSection === "") {
      const foundCard = (state.currentSubjectProgressCards as CardCollection).cards.find(
        (card) => card.id === cardId
      );
      if (foundCard) {
        const cardTopicIds = (foundCard.topics ?? []).map((card) => card.id);
        cardTopicIds.forEach((topicId) => {
          const foundTopicIndex =
            (state.currentSubjectProgressCards as CardCollection).availableTopics.findIndex(
              (topic) => topic.id === topicId
            );
          (state.currentSubjectProgressCards as CardCollection).availableTopics[
            foundTopicIndex
          ].newContent = false;
        });
      }
    } else if (state.currentSubjectSection === "leren-en-oefenen") {
      const foundCard = (state.currentSubjectLearnCards as CardCollection).cards.find(
        (card) => card.id === cardId
      );
      if (foundCard) {
        const cardTopicIds = (foundCard.topics ?? []).map((card) => card.id);
        cardTopicIds.forEach((topicId) => {
          const foundTopicIndex =
            (state.currentSubjectLearnCards as CardCollection).availableTopics.findIndex(
              (topic) => topic.id === topicId
            );
          (state.currentSubjectLearnCards as CardCollection).availableTopics[
            foundTopicIndex
          ].newContent = false;
        });
      }
    } else if (state.currentSubjectSection === "ook-handig") {
      const foundCard = (state.currentSubjectUsefulCards as CardCollection).cards.find(
        (card) => card.id === cardId
      );
      if (foundCard) {
        const cardTopicIds = (foundCard.topics ?? []).map((card) => card.id);
        cardTopicIds.forEach((topicId) => {
          const foundTopicIndex =
            (state.currentSubjectUsefulCards as CardCollection).availableTopics.findIndex(
              (topic) => topic.id === topicId
            );
          (state.currentSubjectUsefulCards as CardCollection).availableTopics[
            foundTopicIndex
          ].newContent = false;
        });
      }
    }
  },
  UPDATE_NEW_CONTENT_STATUS_CARD (state, cardId: number | string) {
    if (state.currentSubjectSection === "") {
      const foundCardIndex = (state.currentSubjectProgressCards as CardCollection).cards.findIndex(
        (card) => card.id === cardId
      );
      (state.currentSubjectProgressCards as CardCollection).cards[
        foundCardIndex
      ].newContent = false;
    } else if (state.currentSubjectSection === "leren-en-oefenen") {
      const foundCardIndex = (state.currentSubjectLearnCards as CardCollection).cards.findIndex(
        (card) => card.id === cardId
      );
      (state.currentSubjectLearnCards as CardCollection).cards[foundCardIndex].newContent = false;
    } else if (state.currentSubjectSection === "ook-handig") {
      const foundCardIndex = (state.currentSubjectUsefulCards as CardCollection).cards.findIndex(
        (card) => card.id === cardId
      );
      (state.currentSubjectUsefulCards as CardCollection).cards[foundCardIndex].newContent = false;
    }
  },
  UPDATE_COMPLETED_CARDS (state, { cardId, topicsToUpdate, userHasLearned }: { cardId: number | string, topicsToUpdate: Topic[], userHasLearned: boolean }) {
    let tmpCards: CardCollection | unknown[] = [];
    if (state.currentSubjectSection === "") {
      tmpCards = state.currentSubjectProgressCards as CardCollection;
    } else if (state.currentSubjectSection === "leren-en-oefenen") {
      tmpCards = state.currentSubjectLearnCards as CardCollection;
    } else if (state.currentSubjectSection === "ook-handig") {
      tmpCards = state.currentSubjectUsefulCards as CardCollection;
    }
    // Previously `(tmpCards as unknown) !== []`, which is always true (compares
    // against a fresh array by reference). Guard on the actual card map instead —
    // this also avoids Object.entries(undefined) when no section matched above.
    if ((tmpCards as CardCollection).cards) {
      for (const [cardKey, card] of Object.entries((tmpCards as CardCollection).cards)) {
        if (card.id === cardId) {
          (tmpCards as CardCollection).cards[cardKey as unknown as number].userHasLearned = !userHasLearned;
        }
      }
      if (tmpCards !== (state as unknown as { current: unknown }).current) {
        state.currentSubjectLearnCards = tmpCards as CardCollection;
      }
    }
    /* The line below is disabled for eslint prefer-const because it throws an error ('tmpCards' is never reassigned. Use 'const' instead  prefer-const) even though we modify the tmpCards variable */
    let tmpTopics =
      (state.currentSubjectLearnCards as CardCollection) &&
      (state.currentSubjectLearnCards as CardCollection).availableTopics
        ? (state.currentSubjectLearnCards as CardCollection).availableTopics
        : []; // eslint-disable-line prefer-const
    // Previously `!== []` on both, which is always true (reference comparison
    // against fresh arrays); check the actual lengths instead.
    if (topicsToUpdate.length && tmpTopics.length) {
      topicsToUpdate.forEach((topicToUpdate) => {
        for (const [topicKey, topic] of Object.entries(tmpTopics)) {
          if (topic.id === topicToUpdate.id) {
            const key = topicKey as unknown as number;
            if (!userHasLearned) {
              if (
                (tmpTopics[key].learnedCards as number) <
                (tmpTopics[key].totalLearnableCards as number)
              ) {
                tmpTopics[key].learnedCards =
                  (tmpTopics[key].learnedCards as number) + 1;
              }
            } else if ((tmpTopics[key].learnedCards as number) > 0) {
              tmpTopics[key].learnedCards =
                (tmpTopics[key].learnedCards as number) - 1;
            }
          }
        }
      });
    }
  },
  RESET_FILTERS (state) {
    state.selectedTopics = [];
    state.selectedParentTopics = [];
    state.selectedSections = [];
    state.selectedExamTypes = [];
    state.selectedDifficultyLevels = [];
  },
};

export const getters: GetterTree<SubjectsState, RootState> = {
  withoutUserSubjects (state, getters, rootState) {
    const auth = (rootState as RootState & { auth: { userSubjects?: UserSubject[] } }).auth
    if (!state.subjects.length || !auth.userSubjects) {
      return state.subjects;
    }
    return state.subjects.filter((subject) => {
      return !auth.userSubjects!.some(
        (userSubject) => subject.id === userSubject.id
      );
    });
  },
  subjectsByLevel (state) {
    if (!state.subjects.length) return [];
    // 1. Grab unique levels from subjects array
    const uniqueLevels = state.subjects
      .map((subject) => {
        return subject.levelName as string;
      })
      .filter((value, idx, self) => self.indexOf(value) === idx);
    // 2. For each unique level, create a new object filled with its corresponding subjects
    //    Then return the unique levels as an array.
    const allLevels: Array<{ levelName: string, subjects: UserSubject[] }> = [];
    uniqueLevels.forEach((level) => {
      const filteredSubjectsPerLevel = state.subjects.filter(
        (subject) => subject.levelName === level
      );
      const obj = {
        levelName: level,
        subjects: filteredSubjectsPerLevel,
      };
      allLevels.push(obj);
    });
    return allLevels.sort(
      (a, b) =>
        levelSortOrder.indexOf(a.levelName) -
        levelSortOrder.indexOf(b.levelName)
    );
  },
  downloadableCards (state) {
    return (state.currentSubjectProgressCards as CardCollection)?.cards?.filter(
      (card) => card.isDownload === true
    );
  },
  subjectBaseUrl (state) {
    return `/mijnexamenbundel/${state.currentSubject?.id}/${state.currentSubject?.slug}`;
  },
  subjectLatestScore (state) {
    return state.currentSubject.latestTestAttempt?.results?.grade;
  },
  checkedTests (state) {
    return state.currentSubject.stream?.tests?.filter(
      (test) => test.latestAttempt !== null
    );
  },
  subjectAvailableTopics (state) {
    return (state.currentSubjectLearnCards as CardCollection)?.availableTopics?.map(
      ({ id, name }) => {
        return {
          id,
          name,
        };
      }
    );
  },
  latestResultsByTopic (state, getters) {
    if (getters.checkedTests.length > 0) {
      const result = (getters.checkedTests as SubjectTest[]).reduce((acc, curr) => {
        const currIsAfter = isAfter(
          parseISO(curr.latestAttempt!.createdAt as string),
          parseISO(acc.latestAttempt!.createdAt as string)
        );
        if (currIsAfter) {
          return curr;
        }
        return acc;
      });
      return result?.latestAttempt?.results?.perTopic;
    }
    return undefined;
  },
  latestTestAttempt (state) {
    return state.currentSubject.latestTestAttempt;
  },
  latestSubjectTestAttempt (state) {
    return (state.subjectTest as SubjectTestDetail).latestAttempt;
  },
  userHasAccessToCurrentSubject (state, getters, rootState, rootGetters) {
    const currentSubjectId = state.currentSubject.id;
    return (
      rootGetters["auth/userSubjectsHasAccess"] &&
      (rootGetters["auth/userSubjectsHasAccess"] as UserSubject[]).some(
        (subject) => subject.id === currentSubjectId
      )
    );
  },
  selectedSectionFilterNames (state) {
    return state.selectedSections.map((section) => section.name).join();
  },
  correctionCard (state) {
    if (
      state.currentSubject.stream?.correctionsCardId &&
      (state.currentSubjectUsefulCards as CardCollection)?.cards
    ) {
      return (state.currentSubjectUsefulCards as CardCollection).cards.find(
        (card) => card.id === state.currentSubject.stream!.correctionsCardId
      );
    }
    return undefined;
  },
  hasCorrection (state, getters) {
    return !!getters.correctionCard;
  },
  latestTestAttemptTopicResultsMap (state, getters) {
    if (((getters.latestTestAttempt as TestAttempt)?.results?.perTopic?.length ?? 0) > 0) {
      return (getters.latestTestAttempt as TestAttempt)?.results?.perTopic?.map(
        ({ topic, percentage, isUncertain }: { topic: Topic, percentage: number, isUncertain: boolean }) => {
          const { id, name } = topic;
          return {
            id,
            name,
            isUncertain,
            score: percentage,
          };
        }
      );
    }

    return null;
  },
  latestSubjectTestAttemptTopicResultsMap (state, getters) {
    if (((getters.latestSubjectTestAttempt as TestAttempt)?.results?.perTopic?.length ?? 0) > 0) {
      return (getters.latestSubjectTestAttempt as TestAttempt)?.results?.perTopic?.map(
        ({ topic, percentage, isUncertain }: { topic: Topic, percentage: number, isUncertain: boolean }) => {
          const { id, name } = topic;
          return {
            id,
            name,
            isUncertain,
            score: percentage,
          };
        }
      );
    }

    return null;
  },
};

export const actions: ActionTree<SubjectsState, RootState> = {
  async get ({ commit }, levels: Array<number | string>) {
    const allLevels: unknown[] = [];
    try {
      for (let idx = 0; idx < levels.length; idx++) {
        const { data } = await this.$axios.get(`/v1/subjects/${levels[idx]}`);
        allLevels.push(data);
      }
      commit("SET_SUBJECTS", allLevels.flat());
    } catch (err) {
      console.error(err);
    }
  },
  async getProfileSubjects ({ commit }, { level, profile }: { level: string, profile: string }) {
    try {
      const { data } = await this.$axios.get(
        `/v1/subjects/${level}/${profile}`
      );
      commit("SET_PRIMARY_SUBJECTS", data);
    } catch (err) {
      console.error(err);
    }
  },
  async getSubject ({ commit }, id: number | string) {
    try {
      const { data } = await this.$axios.get(`/v1/subject/${id}`);
      commit("SET_CURRENT_SUBJECT", data);
    } catch (err) {
      console.error(err);
    }
  },
  async getSubjectCards ({ commit }, { id, filterQuery, page = "index" }: { id: number | string, filterQuery: string, page?: string }) {
    try {
      const { data } = await this.$axios.get(`/v1/subject/${id}/cards${filterQuery}`);
      return new Promise((resolve) => {
        setTimeout(() => {
          commit("SET_CURRENT_SUBJECT_LEARN_LOADING", false);
          if (page === "index") {
            commit("SET_CURRENT_SUBJECT_LEARN_CARDS", data);
            commit("SET_CURRENT_SUBJECT_USEFUL_CARDS", data);
          } else if (page === "progress") {
            commit("SET_CURRENT_SUBJECT_PROGRESS_CARDS", data);
          } else if (page === "general") {
            commit("SET_CURRENT_SUBJECT_USEFUL_CARDS", data);
          }
          resolve(data);
        }, 300);
      });
    } catch (err) {
      console.error(err);
    }
  },
  async getSubjectTests ({ commit }, subjectId: number | string) {
    await this.$axios
      .get(`/v1/subject/${subjectId}/tests`)
      .then((res) => {
        commit("SET_SUBJECT_TESTS", res.data);
      })
      .catch((err) => console.error(err));
  },
  async getSubjectTestById ({ commit }, testId: number | string) {
    await this.$axios
      .get(`/v1/test/${testId}`)
      .then((res) => {
        commit("SET_SUBJECT_TEST", res.data);
      })
      .catch((err) => console.error(err));
  },
  async registerVisitCard ({ commit }, { cardId }: { cardId: number | string, hasNewContent?: boolean }) {
    await this.$axios
      .post(`/v1/users/me/cards/${cardId}/visit`)
      .then(() => {
        // only if the card has new content and user visits it we update the new content status
        // if (hasNewContent) {
        commit("UPDATE_NEW_CONTENT_STATUS_CARD", cardId);
        commit("UPDATE_NEW_CONTENT_STATUS_TOPIC", cardId);
        // }
      })
      .catch((err) => console.error(err));
  },
  completeCard ({ commit }, payload: { cardId: number | string, userHasLearned: boolean, topicsToUpdate: Topic[] }) {
    try {
      if (!payload.userHasLearned) {
        this.$axios.post(`/v1/users/me/cards/${payload.cardId}/learn`);
      } else {
        this.$axios.delete(`/v1/users/me/cards/${payload.cardId}/learn`);
      }
      setTimeout(() => {
        commit("UPDATE_COMPLETED_CARDS", payload);
      }, 350);
    } catch (err) {
      console.error(err);
    }
  },
  async getCard ({ commit }, cardId: number | string) {
    try {
      const { data } = await this.$axios.get(`/v1/card/${cardId}`);
      commit("SET_CURRENT_CARD", data);
    } catch (err) {
      console.error(err);
    }
  },
  async saveExamCheckScores (
    { commit },
    { testId, subjectId, requestBody }: { testId: number | string, subjectId: number | string, requestBody: unknown }
  ) {
    await this.$axios
      .post(`/v1/test/${testId}`, requestBody)
      .then((res) => {
        commit("SET_SUBJECT_TEST_RESULT", res.data);
        commit("UPDATE_CURRENT_SUBJECT", {
          testId,
          updatedTestAttempt: res.data,
        });
        commit(
          "auth/UPDATE_USER_SUBJECTS_TEST_ATTEMPT",
          { subjectId, updatedTestAttempt: res.data },
          { root: true }
        );
      })
      .catch((err) => console.error(err));
  },
  async getLatestAttemptById ({ commit }, testId: number | string) {
    await this.$axios
      .get(`/v1/test/${testId}/latest-attempt`)
      .then((res) => commit("SET_SUBJECT_TEST_ANSWERS", res.data.answers))
      .catch((err) => console.error(err));
  },
};
